import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Pencil,
  Plug,
  Plus,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import {
  ENGINES,
  type DataSourceSummary,
  type DataSourceUpsertInput,
  type Engine,
  type SessionUser,
} from '@idas/shared';
import { api, ApiError } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { EmptyState, ListSkeleton } from '../../components/ui/EmptyState';
import { InlineSpinner, RefreshingOverlay } from '../../components/ui/Refreshing';
import { toast } from '../../components/ui/Toast';

const DEFAULT_PORTS: Record<Engine, number> = { MYSQL: 3306, POSTGRES: 5432, MONGODB: 27017 };

interface TestResult {
  reachable: boolean;
  latencyMs: number | null;
  error: string | null;
  readOnly: boolean | null;
  serverVersion: string | null;
  warning: string | null;
}

export function ConnectionsTab({ me }: { me: SessionUser }) {
  const queryClient = useQueryClient();
  const canWrite = me.permissions.includes('datasource:write');

  const [editing, setEditing] = useState<DataSourceSummary | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DataSourceSummary | null>(null);
  const [tests, setTests] = useState<Record<string, TestResult>>({});

  const datasources = useQuery({ queryKey: ['datasources'], queryFn: api.datasources.list });

  const test = useMutation({
    mutationFn: (id: string) => api.datasources.test(id),
    onSuccess: (result, id) => {
      setTests((current) => ({ ...current, [id]: result }));
      if (!result.reachable) toast.error(result.error ?? 'Could not reach the database.');
      else if (result.readOnly === false) toast.warning('Connected, but the account can write.');
      else toast.success(`Connected in ${result.latencyMs}ms.`);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'The test failed.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.datasources.remove(id),
    onSuccess: () => {
      toast.success('Connection deleted.');
      void queryClient.invalidateQueries({ queryKey: ['datasources'] });
      setConfirmDelete(null);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not delete it.'),
  });

  return (
    <div className="space-y-4">
      <section className="panel rounded-lg">
        <header className="flex items-center gap-2 border-b border-edge px-3 py-2">
          <Database className="h-3.5 w-3.5 text-ink-muted" />
          <h2 className="text-xs font-semibold text-ink">Connections</h2>
          {datasources.isFetching && !datasources.isLoading && <InlineSpinner />}
          <div className="flex-1" />
          {canWrite && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              <Plus className="h-3 w-3" />
              Add connection
            </button>
          )}
        </header>

        {datasources.isLoading && <ListSkeleton rows={3} height={72} />}

        {/* Only once the first load has settled — during it the skeleton is
            already saying the list is empty-for-now. */}
        {!datasources.isLoading && datasources.data?.datasources.length === 0 && (
          <EmptyState
            icon={Database}
            title="No connections yet"
            body="Add the database your analysts should be able to query. Point it at a read replica, using an account granted SELECT and nothing else."
            action={
              canWrite ? (
                <button className="btn btn-primary" onClick={() => setEditing('new')}>
                  <Plus className="h-3 w-3" />
                  Add connection
                </button>
              ) : undefined
            }
          />
        )}

        <RefreshingOverlay active={datasources.isFetching && !datasources.isLoading}>
        <div className="divide-y divide-edge">
          {datasources.data?.datasources.map((source) => {
            const result = tests[source.id];
            return (
              <div key={source.id} className="px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 truncate text-2xs font-medium text-ink">
                      {source.name}
                      {!source.enabled && (
                        <span className="chip border-danger/30 bg-danger/10 text-danger">
                          disabled
                        </span>
                      )}
                    </p>
                    <p className="truncate font-mono text-2xs text-ink-muted">
                      {source.key} · {source.database}
                    </p>
                  </div>

                  <span className="chip border-edge bg-surface-sunken text-ink-muted">
                    {source.engine.toLowerCase()}
                  </span>

                  <span className="text-2xs tabular-nums text-ink-faint">
                    {source.limits.maxRows.toLocaleString()} rows ·{' '}
                    {Math.round(source.limits.timeoutMs / 1000)}s
                  </span>

                  <button
                    className="btn"
                    onClick={() => test.mutate(source.id)}
                    disabled={test.isPending && test.variables === source.id}
                  >
                    <Plug className="h-3 w-3" />
                    {test.isPending && test.variables === source.id ? 'Testing…' : 'Test'}
                  </button>

                  {canWrite && (
                    <>
                      <button
                        className="btn btn-ghost px-1.5"
                        onClick={() => setEditing(source)}
                        aria-label={`Edit ${source.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        className="btn btn-ghost px-1.5 hover:text-danger"
                        onClick={() => setConfirmDelete(source)}
                        aria-label={`Delete ${source.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>

                {result && <TestReport result={result} />}
              </div>
            );
          })}
        </div>
        </RefreshingOverlay>
      </section>

      {editing && (
        <ConnectionModal
          source={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['datasources'] });
            setEditing(null);
          }}
        />
      )}

      {confirmDelete && (
        <Modal
          title={`Delete “${confirmDelete.name}”?`}
          description="This cannot be undone."
          width="sm"
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button className="btn" onClick={() => setConfirmDelete(null)}>
                Keep it
              </button>
              <button
                className="btn border-transparent bg-danger text-white hover:bg-danger/90"
                onClick={() => remove.mutate(confirmDelete.id)}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </>
          }
        >
          <p className="text-2xs leading-relaxed text-ink-muted">
            If any queries have ever run against this connection, deletion is refused — that
            history is evidence and must survive. Disable the connection instead: it blocks every
            new query while keeping the audit trail intact.
          </p>
        </Modal>
      )}
    </div>
  );
}

/**
 * The result of a connection probe.
 *
 * The read-only verdict is given as much weight as reachability, because it is
 * the more consequential of the two. An unreachable database is obvious and
 * self-correcting; an over-privileged account works perfectly and quietly
 * removes a layer of defence nobody will notice is gone.
 */
function TestReport({ result }: { result: TestResult }) {
  if (!result.reachable) {
    return (
      <div className="mt-2 flex items-start gap-1.5 rounded-md border border-danger/30 bg-danger/5 p-2.5 text-2xs leading-relaxed text-danger">
        <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
        <span>{result.error ?? 'Could not connect.'}</span>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'mt-2 flex items-start gap-1.5 rounded-md border p-2.5 text-2xs leading-relaxed',
        result.readOnly === false
          ? 'border-danger/30 bg-danger/5 text-danger'
          : result.readOnly === null
            ? 'border-warn/30 bg-warn/5 text-warn'
            : 'border-ok/30 bg-ok/5 text-ok',
      )}
    >
      {result.readOnly === true ? (
        <CheckCircle2 className="mt-px h-3 w-3 shrink-0" />
      ) : (
        <ShieldAlert className="mt-px h-3 w-3 shrink-0" />
      )}
      <span>
        Connected in {result.latencyMs}ms
        {result.serverVersion && ` · ${result.serverVersion}`}
        {result.readOnly === true && ' · account is read-only'}
        {result.warning && <span className="mt-1 block text-ink">{result.warning}</span>}
      </span>
    </div>
  );
}

function ConnectionModal({
  source,
  onClose,
  onSaved,
}: {
  source: DataSourceSummary | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editingExisting = source !== null;

  // Host, port, username and SSL are not in the summary, so editing has to
  // fetch them. Without them the form would post blanks and the update would
  // be rejected for fields the administrator was never shown.
  const stored = useQuery({
    queryKey: ['datasource-config', source?.id],
    queryFn: () => api.datasources.config(source!.id),
    enabled: editingExisting,
    staleTime: 0,
    gcTime: 0,
  });

  const [form, setForm] = useState<DataSourceUpsertInput>({
    key: source?.key ?? '',
    name: source?.name ?? '',
    engine: source?.engine ?? 'MYSQL',
    host: '',
    port: DEFAULT_PORTS[source?.engine ?? 'MYSQL'],
    database: source?.database ?? '',
    username: '',
    password: '',
    description: source?.description ?? '',
    ssl: false,
    enabled: source?.enabled ?? true,
    maxRows: source?.limits.maxRows ?? 10_000,
    timeoutMs: source?.limits.timeoutMs ?? 30_000,
  });

  // Only the fields the administrator has not touched yet, so a slow response
  // cannot overwrite something they already typed.
  const [touched, setTouched] = useState<Partial<Record<keyof DataSourceUpsertInput, true>>>({});

  useEffect(() => {
    const config = stored.data;
    if (!config) return;
    setForm((current) => ({
      ...current,
      ...(touched.host ? {} : { host: config.host }),
      ...(touched.port ? {} : { port: config.port }),
      ...(touched.username ? {} : { username: config.username }),
      ...(touched.ssl ? {} : { ssl: config.ssl }),
    }));
    // `touched` is read but intentionally not a dependency: this should run
    // when the settings arrive, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored.data]);

  const set = <K extends keyof DataSourceUpsertInput>(key: K, value: DataSourceUpsertInput[K]) => {
    setTouched((current) => ({ ...current, [key]: true }));
    setForm((current) => ({ ...current, [key]: value }));
  };

  const save = useMutation({
    mutationFn: () => {
      // An empty password on edit means "leave the stored one alone"; sending
      // it would overwrite a credential the admin never saw with an empty one.
      const payload: DataSourceUpsertInput = {
        ...form,
        password: form.password ? form.password : undefined,
      };
      return editingExisting
        ? api.datasources.update(source.id, payload)
        : api.datasources.create(payload);
    },
    onSuccess: () => {
      toast.success(editingExisting ? 'Connection updated.' : 'Connection added.');
      onSaved();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not save the connection.'),
  });

  const unsupported = form.engine !== 'MYSQL';

  return (
    <Modal
      title={editingExisting ? `Edit “${source.name}”` : 'Add a connection'}
      description="Credentials are encrypted at rest and never returned by the API."
      width="lg"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            // Saving before the stored settings arrive would post an empty
            // host and username — the very failure this screen used to have.
            disabled={save.isPending || (editingExisting && !stored.data)}
          >
            {save.isPending
              ? 'Saving…'
              : editingExisting && stored.isLoading
                ? 'Loading…'
                : editingExisting
                  ? 'Save changes'
                  : 'Add connection'}
          </button>
        </>
      }
    >
      {stored.error != null && (
        <div className="mb-3 rounded-md border border-danger/30 bg-danger/5 p-2.5 text-2xs leading-relaxed text-danger">
          The current host and username could not be loaded, so this connection cannot be saved
          without overwriting them.{' '}
          {stored.error instanceof ApiError ? stored.error.message : 'Try again in a moment.'}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Display name" hint="What analysts see in the picker.">
          <input
            className="input"
            value={form.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder="Analytics replica"
          />
        </Field>

        <Field label="Key" hint="Immutable identifier used in audit records.">
          <input
            className="input font-mono"
            value={form.key}
            onChange={(event) => set('key', event.target.value)}
            placeholder="analytics-replica"
            disabled={editingExisting}
          />
        </Field>

        <Field label="Engine">
          <select
            className="input"
            value={form.engine}
            onChange={(event) => {
              const engine = event.target.value as Engine;
              set('engine', engine);
              set('port', DEFAULT_PORTS[engine]);
            }}
            disabled={editingExisting}
          >
            {ENGINES.map((engine) => (
              <option key={engine} value={engine}>
                {engine.toLowerCase()}
                {engine !== 'MYSQL' ? ' (not implemented yet)' : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Database">
          <input
            className="input"
            value={form.database}
            onChange={(event) => set('database', event.target.value)}
            placeholder="warehouse"
          />
        </Field>

        <Field label="Host">
          <input
            className="input"
            value={form.host}
            onChange={(event) => set('host', event.target.value)}
            placeholder="replica.internal"
          />
        </Field>

        <Field label="Port">
          <input
            className="input"
            type="number"
            value={form.port}
            onChange={(event) => set('port', Number(event.target.value))}
          />
        </Field>

        <Field label="Username" hint="Must hold SELECT only.">
          <input
            className="input"
            value={form.username}
            onChange={(event) => set('username', event.target.value)}
            placeholder="idas_reader"
            autoComplete="off"
          />
        </Field>

        <Field
          label="Password"
          hint={editingExisting ? 'Leave blank to keep the current one.' : undefined}
        >
          <input
            className="input"
            type="password"
            value={form.password ?? ''}
            onChange={(event) => set('password', event.target.value)}
            autoComplete="new-password"
          />
        </Field>

        <Field label="Row limit" hint="Ceiling for the result grid.">
          <input
            className="input"
            type="number"
            value={form.maxRows}
            onChange={(event) => set('maxRows', Number(event.target.value))}
          />
        </Field>

        <Field label="Timeout (ms)" hint="Statements are killed past this.">
          <input
            className="input"
            type="number"
            value={form.timeoutMs}
            onChange={(event) => set('timeoutMs', Number(event.target.value))}
          />
        </Field>

        <div className="col-span-2">
          <Field label="Description" hint="Optional. Shown to analysts.">
            <input
              className="input"
              value={form.description ?? ''}
              onChange={(event) => set('description', event.target.value)}
              placeholder="Read replica of the production warehouse, ~15 min behind."
            />
          </Field>
        </div>

        <div className="col-span-2 flex gap-4">
          <Checkbox
            label="Use TLS"
            checked={form.ssl ?? false}
            onChange={(value) => set('ssl', value)}
          />
          <Checkbox
            label="Enabled"
            checked={form.enabled ?? true}
            onChange={(value) => set('enabled', value)}
          />
        </div>

        {unsupported && (
          <p className="col-span-2 rounded-md border border-warn/30 bg-warn/5 p-2.5 text-2xs leading-relaxed text-warn">
            The {form.engine.toLowerCase()} driver is not implemented yet. The connection will
            save, but queries against it will be refused until the driver lands.
          </p>
        )}

        <p className="col-span-2 rounded-md border border-edge bg-surface-sunken p-2.5 text-2xs leading-relaxed text-ink-muted">
          Grant this account <code className="font-mono">SELECT</code> and nothing else. The SQL
          guard blocks writes, but it is software — a database that also refuses them is the layer
          that holds when the guard does not.
        </p>
      </div>
    </Modal>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs font-medium text-ink-muted">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-2xs text-ink-faint">{hint}</span>}
    </label>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-2xs text-ink">
      <input
        type="checkbox"
        className="h-3.5 w-3.5 rounded border-edge-strong accent-accent"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}
